"use client";
import { useState, useRef, useEffect } from "react";
import {
  MdClose,
  MdAdd,
  MdSettings,
  MdFormatAlignRight,
  MdFormatAlignCenter,
  MdFormatAlignLeft,
  MdDownload,
  MdFormatBold,
  MdFormatItalic,
  MdSearch,
  MdLogin,
  MdOpenInNew,
} from "react-icons/md";
import { toast } from "react-toastify";
import { useRouter, useSearchParams } from "next/navigation";
import * as htmlToImage from "html-to-image";
import { TbArrowBigUpLineFilled } from "react-icons/tb";
import MemePosterBox from "./MemePosterBox";
import Link from "next/link";
import imageCompression from "browser-image-compression";

const DEFAULT_FONT_SIZE = 35;
const DEFAULT_FONT_FAMILY = "Impact";

const DEFAULT_TEXTS = [
  {
    id: 1,
    content: "Texte 1",
    color: "#ffffff",
    position: { x: 50, y: 10 },
    fontSize: DEFAULT_FONT_SIZE,
    width: 80,
    height: 12,
    isUppercase: true,
    isBold: true,
    isItalic: false,
    rotation: 0,
    textAlign: "center",
    fontFamily: DEFAULT_FONT_FAMILY,
  },
  {
    id: 2,
    content: "Texte 2",
    color: "#ffffff",
    position: { x: 50, y: 90 },
    fontSize: DEFAULT_FONT_SIZE,
    width: 80,
    height: 12,
    isUppercase: true,
    isBold: true,
    isItalic: false,
    rotation: 0,
    textAlign: "center",
    fontFamily: DEFAULT_FONT_FAMILY,
  },
];

const styles = {
  textContainer: {
    position: "absolute",
    minWidth: 40,
    minHeight: 24,
    letterSpacing: 1,
    textShadow: "2px 2px 4px #000, 0 0 2px #000",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    background: "transparent",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    overflow: "visible",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
    width: "fit-content",
    height: "fit-content",
    maxWidth: "100%",
    maxHeight: "100%",
  },
  textContent: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  resizeHandle: {
    position: "absolute",
    width: 20,
    height: 20,
    background: "#fff",
    border: "2px solid #888",
    borderRadius: 4,
    zIndex: 10,
  },
  watermark: {
    position: "absolute",
    bottom: 5,
    right: 5,
    fontSize: "11px",
    color: "rgba(255, 255, 255, 1)",
    fontWeight: "bold",
    pointerEvents: "none",
    zIndex: 5,
    textShadow: "1px 1px 1px #000, 0 0 1px #000",
    letterSpacing: "0.001em",
    opacity: 0.7,
  },
};

const getPositionStyle = (index, total, customPosition) => {
  if (customPosition) {
    return {
      top: `${customPosition.y}%`,
      left: `${customPosition.x}%`,
      transform: "translate(-50%, -50%)",
    };
  }
  const positions = [
    { top: "5%", left: "50%", transform: "translateX(-50%)" },
    { bottom: "5%", left: "50%", transform: "translateX(-50%)" },
    { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
  ];
  if (total === 1) return positions[2];
  if (total === 2) return positions[index === 0 ? 0 : 1];
  if (total === 3) return positions[index];
  return {
    top: `${5 + (90 / (total - 1)) * index}%`,
    left: "50%",
    transform: "translateX(-50%)",
  };
};

const TextField = ({
  text,
  index,
  total,
  onDragStart,
  onTextClick,
  selectedTextId,
  draggedText,
  onResizeStart,
  onRotationDragStart,
}) => {
  const textStyle = {
    ...styles.textContainer,
    width: `${text.width}%`,
    height: `${text.height}%`,
    textAlign: text.textAlign || "center",
    fontWeight: text.isBold ? "bold" : "normal",
    fontStyle: text.isItalic ? "italic" : "normal",
    fontSize: `${text.fontSize}px`,
    color: text.color,
    cursor: draggedText === text.id ? "move" : "pointer",
    border: "none",
    ...getPositionStyle(index, total, text.position),
    transform: `${
      getPositionStyle(index, total, text.position).transform || ""
    } rotate(${text.rotation || 0}deg)`,
  };

  const contentStyle = {
    ...styles.textContent,
    fontSize: `${text.fontSize}px`,
    pointerEvents: "none", // Prevent content from interfering with drag
  };

  // Pour la poignée de rotation
  const rotationHandleOffset = -40; // px au-dessus du texte
  const handleSize = 18;

  return (
    <div
      style={textStyle}
      onMouseDown={(e) => {
        if (
          e.target === e.currentTarget ||
          e.target.parentElement === e.currentTarget
        ) {
          onDragStart(text.id);
        }
      }}
      onTouchStart={(e) => {
        if (
          e.target === e.currentTarget ||
          e.target.parentElement === e.currentTarget
        ) {
          e.preventDefault();
          onDragStart(text.id);
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        onTextClick(text.id);
      }}
    >
      {selectedTextId === text.id && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: "2px dashed #888",
            pointerEvents: "none",
          }}
        />
      )}
      <div style={contentStyle}>
        {text.isUppercase ? text.content.toUpperCase() : text.content}
      </div>
      {selectedTextId === text.id && (
        <>
          {/* Resize handle */}
          <div
            style={{
              ...styles.resizeHandle,
              right: -10,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "ew-resize",
            }}
            className="exclude-me"
            onMouseDown={(e) => onResizeStart(e, text.id, "width")}
            onTouchStart={(e) => onResizeStart(e, text.id, "width")}
          />
          {/* Rotation handle + line */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: rotationHandleOffset,
              transform: "translate(-50%, 0)",
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              pointerEvents: "auto",
            }}
            className="exclude-me"
          >
            {/* Ligne */}
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: handleSize,
                width: 2,
                height: Math.abs(rotationHandleOffset),
                background: "#3ad06d",
                transform: `translate(-50%, 0)`,
              }}
            />
            {/* Poignée cercle */}
            <div
              style={{
                width: handleSize,
                height: handleSize,
                background: "#3ad06d",
                borderRadius: "50%",
                border: "2px solid #1a7f3c",
                cursor: "grab",
                zIndex: 21,
                boxShadow: "0 1px 4px #0002",
              }}
              onMouseDown={(e) => onRotationDragStart(e, text.id)}
              onTouchStart={(e) => onRotationDragStart(e, text.id)}
            />
          </div>
        </>
      )}
    </div>
  );
};

const TextControls = ({
  text,
  index,
  onTextChange,
  onColorChange,
  onTextOptionsToggle,
  onRemoveText,
  onFontSizeChange,
  onTextAlignChange,
  onStyleChange,
  expandedTextId,
  focusedTextId,
  setFocusedTextId,
}) => {
  const blurTimeoutRef = useRef(null);

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setFocusedTextId(null);
    }, 200);
  };

  const handleButtonClick = (e, callback) => {
    e.preventDefault();
    e.stopPropagation();
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    callback();
  };

  return (
    <div
      className="flex flex-col gap-2 mb-3 p-1 border border-base-300 rounded-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-2 ">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={text.color}
              onChange={(e) => onColorChange(text.id, e.target.value)}
              title="Couleur du texte"
              style={{ color: text.color }}
            />
            <input
              type="range"
              min="12"
              max="72"
              value={text.fontSize}
              onChange={(e) => onFontSizeChange(text.id, e.target.value)}
              className="range range-sm w-20"
            />
            <span className="text-sm">{text.fontSize}px</span>
          </div>
          <textarea
            placeholder={`Texte #${index + 1}`}
            value={text.content}
            onChange={(e) => onTextChange(text.id, e.target.value)}
            className="textarea textarea-bordered min-h-16 resize-vertical w-full my-1"
            rows="1"
            onFocus={() => setFocusedTextId(text.id)}
            onBlur={handleBlur}
          />
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-1">
              <button
                className={`btn btn-sm ${text.isUppercase ? "btn-active" : ""}`}
                onClick={(e) =>
                  handleButtonClick(e, () =>
                    onStyleChange(text.id, "isUppercase", !text.isUppercase)
                  )
                }
                title="Majuscules"
              >
                <TbArrowBigUpLineFilled className="w-4 h-4" />
              </button>
              <button
                className={`btn btn-sm ${text.isBold ? "btn-active" : ""}`}
                onClick={(e) =>
                  handleButtonClick(e, () =>
                    onStyleChange(text.id, "isBold", !text.isBold)
                  )
                }
              >
                <MdFormatBold className="w-4 h-4" />
              </button>
              <button
                className={`btn btn-sm ${text.isItalic ? "btn-active" : ""}`}
                onClick={(e) =>
                  handleButtonClick(e, () =>
                    onStyleChange(text.id, "isItalic", !text.isItalic)
                  )
                }
              >
                <MdFormatItalic className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <button
          className="btn btn-ghost btn-xs"
          onClick={(e) => {
            e.stopPropagation();
            onRemoveText(text.id);
          }}
          title="Supprimer ce texte"
        >
          <MdClose className="w-4 h-4" />
        </button>
      </div>
      {/* {(expandedTextId === text.id || focusedTextId === text.id) && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-1">
            <button
              className={`btn btn-sm ${text.isBold ? "btn-active" : ""}`}
              onClick={(e) =>
                handleButtonClick(e, () =>
                  onStyleChange(text.id, "isBold", !text.isBold)
                )
              }
            >
              <MdFormatBold className="w-4 h-4" />
            </button>
            <button
              className={`btn btn-sm ${text.isItalic ? "btn-active" : ""}`}
              onClick={(e) =>
                handleButtonClick(e, () =>
                  onStyleChange(text.id, "isItalic", !text.isItalic)
                )
              }
            >
              <MdFormatItalic className="w-4 h-4" />
            </button>
            <button
              className={`btn btn-sm ${text.isUppercase ? "btn-active" : ""}`}
              onClick={(e) =>
                handleButtonClick(e, () =>
                  onStyleChange(text.id, "isUppercase", !text.isUppercase)
                )
              }
              title="Majuscules"
            >
              <TbArrowBigUpLineFilled className="w-4 h-4" />
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default function MemeGenerateur({ ...props }) {
  const router = useRouter();
  const [showShareModal, setShowShareModal] = useState(false);
  const searchParams = useSearchParams();
  const [templatesQuery, setTemplatesQuery] = useState(
    searchParams.get("template") || ""
  );
  const [image, setImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [texts, setTexts] = useState(DEFAULT_TEXTS);
  const [draggedText, setDraggedText] = useState(null);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [expandedTextId, setExpandedTextId] = useState(null);
  const [resizing, setResizing] = useState({
    id: null,
    startX: 0,
    startY: 0,
    startWidth: 80,
    startHeight: 12,
    direction: null,
  });
  const fileInputRef = useRef();
  const containerRef = useRef();

  // Check if browser is Safari
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
  }, []);

  // --- Rotation drag state ---
  const [rotating, setRotating] = useState({ id: null, center: null });

  const [focusedTextId, setFocusedTextId] = useState(null);
  const blurTimeoutRef = useRef(null);

  function handleImageChange(event) {
    const file = event.target.files[0];
    if (file) {
      setIsImageLoading(true);
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          setImageDimensions({
            width: img.width,
            height: img.height,
          });
          setImage(reader.result);
          setOriginalImage(reader.result);
          setIsImageLoading(false);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  function handleTextChange(id, value) {
    setTexts(texts.map((t) => (t.id === id ? { ...t, content: value } : t)));
  }

  function handleColorChange(id, value) {
    setTexts(texts.map((t) => (t.id === id ? { ...t, color: value } : t)));
  }

  function handleFontSizeChange(id, value) {
    setTexts(
      texts.map((t) => (t.id === id ? { ...t, fontSize: parseInt(value) } : t))
    );
  }

  function handleTextAlignChange(id, value) {
    setTexts(texts.map((t) => (t.id === id ? { ...t, textAlign: value } : t)));
  }

  function handleStyleChange(id, styleType, value) {
    setTexts(
      texts.map((t) => (t.id === id ? { ...t, [styleType]: value } : t))
    );
  }

  function handleDragStart(id) {
    setDraggedText(id);
  }

  function handleDrag(e) {
    if (!draggedText || !containerRef.current) return;
    e.preventDefault();

    const container = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (!clientX || !clientY) return;

    const x = ((clientX - container.left) / container.width) * 100;
    const y = ((clientY - container.top) / container.height) * 100;

    setTexts(
      texts.map((t) =>
        t.id === draggedText ? { ...t, position: { x, y } } : t
      )
    );
  }

  function handleDragEnd() {
    setDraggedText(null);
  }

  function handleTextClick(id) {
    setSelectedTextId(id);
  }

  function handleResizeStart(e, id, direction = "width") {
    e.stopPropagation();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (direction === "width") {
      setResizing({
        id,
        startX: clientX,
        startWidth: texts.find((t) => t.id === id).width,
        direction,
      });
    } else if (direction === "height") {
      setResizing({
        id,
        startY: clientY,
        startHeight: texts.find((t) => t.id === id).height,
        direction,
      });
    }
  }

  function handleResize(e) {
    if (!resizing.id) return;
    e.preventDefault();

    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    setTexts((prevTexts) => {
      return prevTexts.map((t) => {
        if (t.id !== resizing.id) return t;
        if (resizing.direction === "width") {
          const deltaX = clientX - resizing.startX;
          const deltaPercent = (deltaX / 500) * 100;
          let newWidth = Math.max(
            10,
            Math.min(100, resizing.startWidth + deltaPercent)
          );
          return {
            ...t,
            width: newWidth,
          };
        } else if (resizing.direction === "height") {
          const deltaY = clientY - resizing.startY;
          const deltaPercent = (deltaY / 500) * 100;
          return {
            ...t,
            height: Math.max(
              5,
              Math.min(50, resizing.startHeight + deltaPercent)
            ),
          };
        }
        return t;
      });
    });
  }

  function handleResizeEnd() {
    setResizing({
      id: null,
      startX: 0,
      startY: 0,
      startWidth: 80,
      startHeight: 12,
      direction: null,
    });
  }

  // --- Resize global listeners ---
  useEffect(() => {
    if (!resizing.id) return;

    function onMove(e) {
      handleResize(e);
    }
    function onUp(e) {
      handleResizeEnd();
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [resizing.id]);

  function addTextField() {
    const newId = Date.now();
    setTexts([
      ...texts,
      {
        id: newId,
        content: `Texte ${texts.length + 1}`,
        color: "#ffffff",
        position: { x: 50, y: 50 },
        fontSize: DEFAULT_FONT_SIZE,
        width: 80,
        height: 12,
        isUppercase: true,
        isBold: true,
        isItalic: false,
        rotation: 0,
        textAlign: "center",
      },
    ]);
  }

  function removeTextField(id) {
    setTexts(texts.length > 1 ? texts.filter((t) => t.id !== id) : texts);
  }

  function handleReset() {
    setImage(null);
    setOriginalImage(null);
    setImageDimensions({
      width: 0,
      height: 0,
    });
    setTexts(DEFAULT_TEXTS);
    setTemplateId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function toggleTextOptions(id) {
    setExpandedTextId(expandedTextId === id ? null : id);
  }

  function handleRotationChange(id, value) {
    setTexts(texts.map((t) => (t.id === id ? { ...t, rotation: value } : t)));
  }

  // Calcul du centre du texte pour la rotation
  function getTextFieldCenter(text) {
    if (!containerRef.current) return { x: 0, y: 0 };
    const container = containerRef.current.getBoundingClientRect();
    const x = container.left + container.width * (text.position.x / 100);
    const y = container.top + container.height * (text.position.y / 100);
    return { x, y };
  }

  function handleRotationDragStart(e, id) {
    e.stopPropagation();
    const text = texts.find((t) => t.id === id);
    const center = getTextFieldCenter(text);
    setRotating({ id, center });
  }

  function handleRotationDrag(e) {
    if (!rotating.id) return;
    e.preventDefault();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    if (clientX == null || clientY == null) return;
    const { x: cx, y: cy } = rotating.center;
    const angle = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
    // 0° = horizontal droite, on veut 0° = haut
    let rotation = angle + 90;
    if (rotation < 0) rotation += 360;
    setTexts((texts) =>
      texts.map((t) =>
        t.id === rotating.id ? { ...t, rotation: Math.round(rotation) } : t
      )
    );
  }

  function handleRotationDragEnd() {
    setRotating({ id: null, center: null });
  }

  // Listeners globaux pour rotation
  useEffect(() => {
    if (!rotating.id) return;
    function onMove(e) {
      handleRotationDrag(e);
    }
    function onUp(e) {
      handleRotationDragEnd();
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [rotating.id]);

  function handleRotationInputChange(id, value) {
    setTexts(texts.map((t) => (t.id === id ? { ...t, rotation: value } : t)));
  }

  async function downloadMeme() {
    const filter = (node) => {
      const exclusionClasses = ["exclude-me"];
      return !exclusionClasses.some((classname) =>
        node.classList?.contains(classname)
      );
    };

    try {
      // Ensure fonts are loaded before generating the image
      await document.fonts.ready;

      const dataUrl = await htmlToImage.toJpeg(containerRef.current, {
        quality: 0.9,
        includeQueryParams: true,
        filter,
        fontEmbedCSS: true,
      });

      // Convertir dataUrl en Blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Options d'optimisation
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: "image/jpeg",
      };

      // Optimisation de l'image
      const compressedBlob = await imageCompression(blob, options);

      // Convertir le blob compressé en dataUrl
      const compressedDataUrl = URL.createObjectURL(compressedBlob);

      var link = document.createElement("a");
      link.download = "choualbox-meme.jpeg";
      link.href = compressedDataUrl;
      link.click();
      toast.success("Téléchargé avec succès");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
      console.error("Download error:", error);
    }
  }

  const [templates, setTemplates] = useState([]);
  const [templateSort, setTemplateSort] = useState(null);

  const getTemplates = async () => {
    var bodyFormData = new FormData();

    if (props.userTk) bodyFormData.append("userTk", props.userTk);
    bodyFormData.append("page", 0);
    if (templateSort) bodyFormData.append("tri", templateSort);

    const res = await fetch(
      process.env.NEXT_PUBLIC_API_URL + "/api/generateur/boucle",
      {
        method: "POST",
        body: bodyFormData,
        // headers: { "User-Agent": "Choualbox Next" },
      }
    );

    let response = await res.json();
    if (!response) {
      return notFound();
    }
    if (response.status == 200) {
      setTemplates(response.data);
    }
  };

  const [templatesQueryResults, setTemplatesQueryResults] = useState([]);
  const getTemplatesQuery = async () => {
    var bodyFormData = new FormData();

    if (props.userTk) bodyFormData.append("userTk", props.userTk);
    bodyFormData.append("page", 0);
    bodyFormData.append("q", templatesQuery);
    const res = await fetch(
      process.env.NEXT_PUBLIC_API_URL + "/api/generateur/boucle",
      {
        method: "POST",
        body: bodyFormData,
        // headers: { "User-Agent": "Choualbox Next" },
      }
    );

    let response = await res.json();
    if (!response) {
      return notFound();
    }
    if (response.status == 200) {
      setTemplatesQueryResults(response.data);
    }
  };

  useEffect(() => {
    if (templates.length == 0) {
      getTemplates();
    }
  }, [templates]);

  useEffect(() => {
    getTemplates();
  }, [templateSort]);

  useEffect(() => {
    if (templatesQuery) {
      getTemplatesQuery();
    } else {
      setTemplatesQueryResults([]);
    }
  }, [templatesQuery]);

  const [templateId, setTemplateId] = useState(null);

  function handleTemplateChange(template) {
    setIsImageLoading(true);
    // Utiliser l'image originale pour le template
    const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(
      template.image
    )}`;

    // Créer une nouvelle image pour obtenir les dimensions
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      setImageDimensions({
        width: img.width,
        height: img.height,
      });
      setImage(proxyUrl);
      setIsImageLoading(false);
    };

    img.onerror = () => {
      console.error("Erreur de chargement de l'image:", template.image);
      toast.error("Erreur lors du chargement de l'image");
      setIsImageLoading(false);
    };

    img.src = proxyUrl;

    setImage(proxyUrl);
    setTemplateId(template.id);
    setTemplatesQuery("");
    setTemplatesQueryResults([]);
  }

  return (
    <div
      className="flex flex-col md:flex-row gap-2"
      onClick={() => setSelectedTextId(null)}
    >
      {/* Meme Preview */}
      <div className="flex-1 flex flex-col items-center gap-2">
        {isSafari && (
          <div className="alert alert-error mb-2">
            Actuellement le générateur de mèmes n&apos;est pas compatible avec
            Safari. Vous pouvez utiliser Chrome ou Firefox.
          </div>
        )}
        {!image ? (
          <>
            <div className="flex flex-col-reverse gap-2 w-full sm:flex-row">
              <label className="input input-md w-full input-bordered flex items-center gap-2 relative">
                <MdSearch />
                <input
                  type="search"
                  placeholder="Rechercher un template..."
                  className="w-full"
                  value={templatesQuery}
                  onChange={(e) => setTemplatesQuery(e.target.value)}
                />
                {templatesQueryResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {templatesQueryResults.map((template) => (
                      <div
                        key={template.permalink + template.id}
                        className="p-2 hover:bg-base-200 cursor-pointer flex items-center gap-2"
                        onClick={() => handleTemplateChange(template)}
                      >
                        <img
                          src={`/api/proxy-image?url=${encodeURIComponent(
                            template.miniature
                          )}`}
                          alt="Template"
                          className="w-12 h-12 object-cover rounded"
                        />
                        <span className="text-sm truncate">
                          {template.titre || "Template"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </label>
              <label className="btn btn-md">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <MdAdd className="w-6 h-6" />
                Uploader
              </label>
            </div>

            <div role="tablist" className="tabs tabs-box w-full">
              <button
                role="tab"
                className={`tab ${!templateSort && "tab-active"}`}
                onClick={() => setTemplateSort(null)}
              >
                Populaires
              </button>
              <button
                role="tab"
                className={`tab ${templateSort === "date" && "tab-active"}`}
                onClick={() => setTemplateSort("date")}
              >
                Nouveaux
              </button>
              <a
                target="_blank"
                href="/generateur"
                className="tab flex items-center gap-1"
              >
                Tout <MdOpenInNew className="w-4 h-4 ml-1" />
              </a>
            </div>

            {templates.length > 0 ? (
              <div className="mt-2">
                <div className="grid grid-cols-3 gap-2">
                  {templates.slice(0, 12).map((template) => (
                    <img
                      key={template.permalink + template.id}
                      src={`/api/proxy-image?url=${encodeURIComponent(
                        template.miniature
                      )}`}
                      alt="Template"
                      className="w-full aspect-square object-cover cursor-pointer rounded-lg"
                      onClick={() => handleTemplateChange(template)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="loading loading-spinner text-base-content/50">
                Aucun template trouvé
              </div>
            )}
          </>
        ) : (
          <div
            ref={containerRef}
            className="relative bg-base-200  touch-none max-w-full"
            onMouseMove={handleDrag}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchMove={handleDrag}
            onTouchEnd={handleDragEnd}
            onTouchCancel={handleDragEnd}
            onClick={() => setSelectedTextId(null)}
          >
            {isImageLoading ? (
              <div className="relative min-h-96 w-full">
                <div className="skeleton min-h-[800px] w-full"></div>
              </div>
            ) : (
              <>
                <img
                  src={image}
                  alt="Meme"
                  className="w-full h-full object-contain"
                  style={{ userSelect: "none", pointerEvents: "none" }}
                />
                {texts.map(
                  (text, idx) =>
                    text.content.trim() && (
                      <TextField
                        key={text.id}
                        text={text}
                        index={idx}
                        total={texts.length}
                        onDragStart={handleDragStart}
                        onTextClick={handleTextClick}
                        selectedTextId={selectedTextId}
                        draggedText={draggedText}
                        onResizeStart={handleResizeStart}
                        onRotationDragStart={handleRotationDragStart}
                        onRotationInputChange={handleRotationInputChange}
                      />
                    )
                )}
                <div style={styles.watermark}>choualbox</div>
                <button
                  className="absolute top-2 right-2 btn btn-sm btn-circle btn-neutral exclude-me"
                  onClick={handleReset}
                  title="Réinitialiser"
                >
                  <MdClose className="w-4 h-4" />
                </button>
                <button
                  className="absolute top-14 right-2 btn btn-sm btn-circle btn-neutral exclude-me"
                  onClick={downloadMeme}
                  disabled={!image}
                >
                  <MdDownload className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {/* Text Controls */}
      <div className="flex-1 w-full">
        {texts.map((text, idx) => (
          <TextControls
            key={text.id}
            text={text}
            index={idx}
            onTextChange={handleTextChange}
            onColorChange={handleColorChange}
            onTextOptionsToggle={toggleTextOptions}
            onRemoveText={removeTextField}
            onFontSizeChange={handleFontSizeChange}
            onTextAlignChange={handleTextAlignChange}
            onStyleChange={handleStyleChange}
            expandedTextId={expandedTextId}
            focusedTextId={focusedTextId}
            setFocusedTextId={setFocusedTextId}
          />
        ))}
        <button className="btn btn-sm " onClick={addTextField}>
          <MdAdd className="w-4 h-4 mr-1" /> Ajouter un texte
        </button>

        <div className="flex gap-2 mt-6 mb-4">
          {props.userTk ? (
            <button
              className="btn btn-primary flex-1"
              onClick={() => setShowShareModal(true)}
              disabled={!image}
            >
              Partager
            </button>
          ) : (
            <button
              className="btn btn-primary flex-1"
              onClick={() => router.push("/m/connexion")}
              disabled={!image}
            >
              <MdLogin />
              Connectez-vous pour partager
            </button>
          )}
        </div>

        <MemePosterBox
          showShareModal={showShareModal}
          setShowShareModal={setShowShareModal}
          userTk={props.userTk}
          image={image}
          containerRef={containerRef}
          templateId={templateId}
          text1={texts[0]}
          text2={texts[1]}
          originalImage={originalImage}
        />
      </div>
    </div>
  );
}
